import React, { useEffect, useState } from "react";
import { Check, Copy, Plus } from "lucide-react";
import {
  maintenanceApi,
  ZitadelProjectRole,
  ZitadelCreateUserResp,
} from "../../../api/maintenance";
import "./zitadel-provisioning.scss";
import { useNotice } from "../shared/useNotice";

/** Shared bits used across the Zitadel provisioning flows. */

/** Backend may return detail as a string or { message, zitadel }. */
export function errText(err: any, fallback: string): string {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (typeof d === "object") {
    const z = d.zitadel?.message || "";
    return [d.message, typeof z === "string" ? z : ""]
      .filter(Boolean)
      .join(" — ");
  }
  return fallback;
}

export const CopyBtn: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="maint-btn-small"
      title="Copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable */
        }
      }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
};

/** Fetch the Admin Console project role catalog once. */
export function useProjectRoles(): {
  roles: ZitadelProjectRole[];
  rolesError: string;
} {
  const [roles, setRoles] = useState<ZitadelProjectRole[]>([]);
  const [rolesError, setRolesError] = useState("");
  useEffect(() => {
    maintenanceApi
      .zitadelGetProjectRoles()
      .then((r) => setRoles(r.data))
      .catch((e) => setRolesError(errText(e, "Failed to load project roles")));
  }, []);
  return { roles, rolesError };
}

export const RolePicker: React.FC<{
  roles: ZitadelProjectRole[];
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ roles, selected, onChange }) => (
  <div className="ztl-roles">
    {roles.map((r) => (
      <label key={r.key}>
        <input
          type="checkbox"
          checked={selected.includes(r.key)}
          onChange={() =>
            onChange(
              selected.includes(r.key)
                ? selected.filter((k) => k !== r.key)
                : [...selected, r.key]
            )
          }
        />
        {r.display_name || r.key}
      </label>
    ))}
    {roles.length === 0 && (
      <span style={{ fontSize: 13, color: "#888" }}>No roles loaded</span>
    )}
  </div>
);

/**
 * Add-user form + post-create password reveal. Used by the onboarding
 * flows (where several users may be added before moving on).
 */
export const UserCreatePanel: React.FC<{
  orgZitadelId: string;
  roles: ZitadelProjectRole[];
  /** org platform URL — the credentials email links to <loginUrl>/login */
  loginUrl?: string;
  orgName?: string;
  onChange?: (createdCount: number) => void;
}> = ({ orgZitadelId, roles, loginUrl, orgName, onChange }) => {
  const [f, setF] = useState({
    email: "",
    first_name: "",
    last_name: "",
    username: "",
    initial_password: "",
  });
  const [picked, setPicked] = useState<string[]>([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useNotice("error", "");
  const [created, setCreated] = useState<ZitadelCreateUserResp[]>([]);

  useEffect(() => {
    if (roles.find((r) => r.key === "org-admin")) setPicked(["org-admin"]);
  }, [roles]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.email || !f.first_name || !f.last_name)
      return setError("Email, first and last name are required");
    if (picked.length === 0) return setError("Pick at least one role");
    setBusy(true);
    setError("");
    try {
      const r = await maintenanceApi.zitadelCreateUser(orgZitadelId, {
        email: f.email.trim(),
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        username: f.username.trim() || undefined,
        initial_password: f.initial_password.trim() || undefined,
        role_keys: picked,
        send_email: sendEmail,
        login_url: loginUrl || undefined,
        org_name: orgName || undefined,
      });
      const next = [...created, r.data];
      setCreated(next);
      onChange?.(next.length);
      setF({
        email: "",
        first_name: "",
        last_name: "",
        username: "",
        initial_password: "",
      });
    } catch (err) {
      setError(errText(err, "Failed to create user"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {created.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {created.map((cu) => (
            <div key={cu.user_id}>
              <div className="ztl-user-row">
                <span>
                  <b>{cu.username}</b> — {cu.email}
                </span>
                <span style={{ color: "#888" }}>
                  {cu.role_keys.join(", ")}
                </span>
              </div>
              <div className="ztl-secret" style={{ margin: "6px 0 4px" }}>
                <span style={{ fontSize: 12, color: "#888" }}>
                  Initial password (change on first login):
                </span>
                <code>{cu.initial_password}</code>
                <CopyBtn value={cu.initial_password} />
              </div>
              <p
                style={{
                  fontSize: 12,
                  margin: "0 0 12px",
                  color: cu.email_sent ? "#2e7d32" : "#b26a00",
                }}
              >
                {cu.email_sent
                  ? `✓ Credentials emailed to ${cu.email}`
                  : `⚠ Email not sent${
                      cu.email_error ? ` (${cu.email_error})` : ""
                    } — share the password manually`}
              </p>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={submit}>
        <div className="maint-form-row">
          <label className="ztl-field" style={{ flex: 1 }}>
            <span>Email *</span>
            <input
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
            />
          </label>
          <label className="ztl-field" style={{ flex: 1 }}>
            <span>Username</span>
            <input
              value={f.username}
              onChange={(e) => setF({ ...f, username: e.target.value })}
              placeholder="defaults to email local part"
            />
          </label>
        </div>
        <div className="maint-form-row">
          <label className="ztl-field" style={{ flex: 1 }}>
            <span>First name *</span>
            <input
              value={f.first_name}
              onChange={(e) => setF({ ...f, first_name: e.target.value })}
            />
          </label>
          <label className="ztl-field" style={{ flex: 1 }}>
            <span>Last name *</span>
            <input
              value={f.last_name}
              onChange={(e) => setF({ ...f, last_name: e.target.value })}
            />
          </label>
        </div>
        <label className="ztl-field">
          <span>Initial password (optional — generated if blank)</span>
          <input
            value={f.initial_password}
            onChange={(e) =>
              setF({ ...f, initial_password: e.target.value })
            }
          />
        </label>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Roles</span>
        <RolePicker roles={roles} selected={picked} onChange={setPicked} />
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            margin: "12px 0 0",
          }}
        >
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
          />
          Email credentials to the user
          {sendEmail && !loginUrl && (
            <span style={{ color: "#b26a00", fontSize: 12 }}>
              (no platform URL — link may be generic)
            </span>
          )}
        </label>
        {error && <p className="maint-form-error">{error}</p>}
        <div className="maint-form-actions" style={{ marginTop: 12 }}>
          <button
            type="submit"
            disabled={busy}
            className="maint-btn-secondary"
          >
            <Plus size={16} /> {busy ? "Creating..." : "Add user"}
          </button>
        </div>
      </form>
    </div>
  );
};
